import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal, { useModal } from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import { loadStripe } from '@stripe/stripe-js';
import { stripeErrorMessage } from '../utils/stripeErrors';

export function isBillingComplete(user: any): boolean {
  if (!user || !user.billing_type) return false;
  const base = !!(user.billing_address && user.billing_city && user.billing_zip && user.billing_province);
  if (!base) return false;
  if (user.billing_type === 'azienda') {
    if (!(user.sdi_code || user.pec_email)) return false;
    return !!(user.billing_company && user.vat_number);
  }
  return !!(user.billing_first_name && user.billing_last_name && user.tax_code);
}

export function missingBillingFields(user: any): string[] {
  if (!user) return ['Nessun dato inserito'];
  const missing: string[] = [];
  if (!user.billing_type) missing.push('Tipo intestatario');
  if (!user.billing_address) missing.push('Indirizzo');
  if (!user.billing_city) missing.push('Città');
  if (!user.billing_zip) missing.push('CAP');
  if (!user.billing_province) missing.push('Provincia');
  if (user.billing_type === 'azienda') {
    if (!user.billing_company) missing.push('Ragione Sociale');
    if (!user.vat_number) missing.push('Partita IVA');
    if (!user.sdi_code && !user.pec_email) missing.push('Codice SDI o PEC');
  } else {
    if (!user.billing_first_name) missing.push('Nome');
    if (!user.billing_last_name) missing.push('Cognome');
    if (!user.tax_code) missing.push('Codice Fiscale');
  }
  return missing;
}

const PLAN_META: Record<string, { icon: string; name: string; period: string }> = {
  trial:   { icon: 'science',            name: 'Piano Trial',   period: 'Una tantum · 8 giorni' },
  base:    { icon: 'rocket_launch',      name: 'Piano Base',    period: 'Abbonamento mensile' },
  medium:  { icon: 'star',              name: 'Piano Medium',  period: 'Abbonamento mensile' },
  premium: { icon: 'workspace_premium', name: 'Piano Premium', period: 'Abbonamento mensile' },
};

const IVA = 0.22;

interface SavedCard { brand: string; last4: string; exp_month: number; exp_year: number; }

/**
 * Pagamento di un piano. Se l'utente ha già una carta salvata la usa senza
 * farla reinserire (con la possibilità di cambiarla); altrimenti mostra qui il
 * modulo carta: non si viene mai rimandati al profilo. Ogni attesa del server è
 * coperta da un caricamento a tutto schermo e il risultato arriva in un modale.
 */
export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { modal, show, close } = useModal();

  const billingOk = isBillingComplete(user);
  const billingMissing = billingOk ? [] : missingBillingFields(user);

  const planId = searchParams.get('plan') || 'base';
  const planPrice = parseInt(searchParams.get('price') || '0');
  const meta = PLAN_META[planId];
  const vatAmount = Math.round(planPrice * IVA * 100) / 100;
  const totalAmount = planPrice + vatAmount;

  const stripeRef = useRef<any>(null);
  const cardRef = useRef<any>(null);
  const cardMountRef = useRef<HTMLDivElement>(null);

  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [cardChecked, setCardChecked] = useState(false);   // risposta su carta salvata arrivata
  const [useNewCard, setUseNewCard] = useState(false);
  const [holderName, setHolderName] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const [paying, setPaying] = useState(false);
  const [stripeKeyError, setStripeKeyError] = useState<string | null>(null);

  const showCardForm = cardChecked && (!savedCard || useNewCard);

  // 1) Carta già salvata?
  useEffect(() => {
    let mounted = true;
    subscriptionApi.paymentMethod()
      .then(r => { if (mounted) setSavedCard(r.data.card || null); })
      .catch(() => { if (mounted) setSavedCard(null); })
      .finally(() => { if (mounted) setCardChecked(true); });
    return () => { mounted = false; };
  }, []);

  // 2) Modulo carta Stripe, solo quando serve davvero
  useEffect(() => {
    if (!showCardForm) return;
    let mounted = true;
    setCardReady(false);
    setStripeKeyError(null);
    api.get('/stripe-key')
      .then(res => {
        if (!mounted) return;
        const pk = res.data?.publishable_key;
        if (!pk) {
          setStripeKeyError('Pagamenti momentaneamente non disponibili. Riprova più tardi.');
          return;
        }
        return loadStripe(pk);
      })
      .then(stripe => {
        if (!stripe || !mounted || !cardMountRef.current) return;
        stripeRef.current = stripe;
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: {
              color: '#E2E8F5',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '15px',
              fontSmoothing: 'antialiased',
              '::placeholder': { color: '#4E5A72' },
              iconColor: '#4F72FF',
            },
            invalid: { color: '#F87171' },
          },
          hidePostalCode: true,
        });
        card.mount(cardMountRef.current);
        card.on('ready', () => mounted && setCardReady(true));
        cardRef.current = card;
      })
      .catch(() => {
        if (mounted) setStripeKeyError('Impossibile caricare il modulo di pagamento. Riprova più tardi.');
      });
    return () => {
      mounted = false;
      cardRef.current?.destroy();
      cardRef.current = null;
    };
  }, [showCardForm]);

  const attivato = async () => {
    await refreshUser();
    setPaying(false);
    show({
      title: 'Abbonamento attivato!',
      message: `${meta?.name || 'Il piano'} è attivo. Hai pagato €${totalAmount.toFixed(2)} (IVA inclusa)${planId === 'trial' ? '' : ', il rinnovo è automatico ogni mese'}. Ora puoi pubblicare i tuoi contenuti.`,
      type: 'success',
      confirmText: 'Vai alla home',
      onConfirm: () => { close(); navigate('/'); },
    });
  };

  const fallito = (message: string) => {
    setPaying(false);
    show({ title: 'Pagamento non riuscito', message, type: 'error', onConfirm: close });
  };

  const handlePay = async () => {
    if (paying) return;
    if (showCardForm) {
      if (!stripeRef.current || !cardRef.current) return;
      if (!holderName.trim()) {
        show({ title: 'Dati mancanti', message: 'Inserisci il nome dell\'intestatario della carta.', type: 'warning', onConfirm: close });
        return;
      }
    }
    setPaying(true);
    try {
      let paymentMethodId: string | undefined;
      if (showCardForm) {
        const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
          type: 'card',
          card: cardRef.current,
          billing_details: { name: holderName.trim() },
        });
        if (error) { fallito(stripeErrorMessage(error)); return; }
        paymentMethodId = paymentMethod.id;
      }
      await subscriptionApi.subscribe(paymentMethodId ? { plan: planId, payment_method: paymentMethodId } : { plan: planId });
      await attivato();
    } catch (err: any) {
      fallito(err.response?.data?.error || 'Si è verificato un errore. Controlla i dati e riprova.');
    }
  };

  const puoPagare = billingOk && !paying && cardChecked && (!showCardForm || (cardReady && !stripeKeyError));

  return (
    <div className="page">
      {!cardChecked && <LoadingOverlay title="Preparazione del pagamento..." subtitle="Controlliamo il tuo metodo di pagamento" icon="credit_card" />}
      {paying && <LoadingOverlay title="Pagamento in corso..." subtitle="Non chiudere la pagina, ci vuole qualche secondo" icon="lock" />}
      {showCardForm && !cardReady && !stripeKeyError && <LoadingOverlay title="Caricamento del modulo carta..." subtitle="Un momento" icon="credit_card" />}

      {modal && (
        <Modal isOpen title={modal.title} message={modal.message} type={modal.type}
          confirmText={modal.confirmText} cancelText={modal.cancelText}
          onConfirm={modal.onConfirm} onCancel={modal.onCancel ?? close} />
      )}

      <div className="top-nav" style={{ marginBottom: 0 }}>
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="mi mi-20">arrow_back</span>
        </button>
        <span className="nav-title">Pagamento</span>
      </div>

      {/* Piano selezionato */}
      <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--primary-grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span className="mi mi-22" style={{ color: '#fff' }}>{meta?.icon}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{meta?.name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{meta?.period}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>€{planPrice}</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>{planId === 'trial' ? 'una tantum' : '/mese'}</div>
        </div>
      </div>

      {/* Riepilogo IVA */}
      <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
          <span>Imponibile</span><span>€{planPrice.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)' }}>
          <span>IVA 22%</span><span>€{vatAmount.toFixed(2)}</span>
        </div>
        <div style={{ height: 1, background: 'var(--border)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>
          <span>Totale</span><span>€{totalAmount.toFixed(2)}</span>
        </div>
      </div>

      {/* Avviso fatturazione incompleta */}
      {!billingOk && (
        <div className="alert warning" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="mi mi-18">receipt_long</span>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Dati di fatturazione mancanti</span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--warning)', margin: 0 }}>
            Mancano: {billingMissing.join(', ')}.
          </p>
          <button className="btn btn-secondary" style={{ fontSize: 13, padding: '9px 16px', width: 'auto' }}
            onClick={() => navigate(`/billing?return=/payment?plan=${planId}%26price=${planPrice}`)}>
            <span className="mi mi-18">edit</span> Inserisci dati fatturazione
          </button>
        </div>
      )}

      {/* Metodo di pagamento */}
      {cardChecked && savedCard && !useNewCard && (
        <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span className="section-label" style={{ margin: 0 }}>Metodo di pagamento</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="mi" style={{ fontSize: 28, color: 'var(--primary)' }}>credit_card</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{savedCard.brand?.toUpperCase()} •••• {savedCard.last4}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Scade {String(savedCard.exp_month).padStart(2, '0')}/{savedCard.exp_year}</div>
            </div>
            <span className="badge badge-success" style={{ padding: '3px 8px', fontSize: 11 }}>
              <span className="mi mi-14">check_circle</span>&nbsp;Salvata
            </span>
          </div>
          <button className="btn btn-outline" style={{ fontSize: 13, padding: 10 }} onClick={() => setUseNewCard(true)}>
            <span className="mi mi-18">credit_card</span> Usa un'altra carta
          </button>
        </div>
      )}

      {showCardForm && (
        stripeKeyError ? (
          <div className="alert error">
            <span className="mi mi-18">error_outline</span>
            <span>{stripeKeyError}</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Intestatario carta</label>
              <input
                className="form-input"
                type="text"
                value={holderName}
                onChange={e => setHolderName(e.target.value)}
                placeholder="Mario Rossi"
                autoComplete="cc-name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Dati carta</label>
              <div
                ref={cardMountRef}
                style={{
                  background: 'var(--s2)',
                  border: '1px solid var(--border-md)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '14px 16px',
                  minHeight: 48,
                }}
              />
            </div>
            {savedCard && (
              <button className="btn btn-cancel" style={{ fontSize: 13, padding: 10 }} onClick={() => setUseNewCard(false)}>
                Usa la carta salvata •••• {savedCard.last4}
              </button>
            )}
          </div>
        )
      )}

      {/* Sicurezza */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--s2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
        <span className="mi mi-18" style={{ color: 'var(--success)' }}>lock</span>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
          Pagamento sicuro via <strong style={{ color: 'var(--text-1)' }}>Stripe</strong> · I tuoi dati sono protetti
        </span>
      </div>

      <button className="btn btn-primary" style={{ fontSize: 15, padding: 17 }} onClick={handlePay} disabled={!puoPagare}>
        <span className="mi mi-20">lock</span> Paga €{totalAmount.toFixed(2)} e attiva
      </button>

      <button className="btn btn-cancel" onClick={() => navigate(-1)} disabled={paying}>
        Annulla
      </button>
    </div>
  );
}
